import type { NextPage } from 'next'
import Head from 'next/head'
import Hero from '@/components/sections/Hero'
import PostTease from '@/components/teases/PostTease'
import { Headline2 } from '@/components/common/Headline2'
import { PlanetDecor } from '@/components/decors/PlanetDecor'
import { Button } from '@/components/controls/Button'
import { CallToAction } from '@/components/common/CallToAction'
import { formatDate } from '@/utils/date'
import { FakeNav } from '@/components/common/FakeNav'
import type { Post } from '@/types/index'
import Masonry from 'react-masonry-css'
import qs from 'qs'

import { ImageLoaderProps } from 'next/image'
import blog_page from '@/public/svg/blog_page.svg'
import blog_page_img from '@/public/images/site-preview.gif'
import Image from 'next/image'
import { useState } from 'react'
import { Breadcrumbs } from '@/components/navigation/Breadcrumbs'
import { useQuery } from '@/utils/url'

const loader = ({ src, width, quality }: ImageLoaderProps) => {
  return `${process.env.NEXT_PUBLIC_SITE}${src}?w=${width}&q=${quality || 75}`
}

const getPostsAndFormat = async (url: string, start: number, limit: number) => {
  const query = qs.stringify({
    fields: ['title', 'slug', 'excerpt', 'date'],
    sort: ['publishedAt:desc'],
    pagination: {
      start: start,
      limit: limit,
      withCount: true
    },
    populate: {
      thumbnail: {
        fields: ['*'],
      },
    },
    filters: {
      featured: {
        $ne: true,
      },
    },
  }, {
    encodeValuesOnly: true
  })

  const res = await fetch(`${url}/posts?${query}`)
  const data = await res.json()

  const posts = data.data.map((post: any) => {
    return {
      id: post.id,
      title: post.attributes.title,
      slug: post.attributes.slug,
      date: formatDate(post.attributes.date),
      excerpt: post.attributes.excerpt,
      thumbnail: {
        url: post.attributes.thumbnail.data.attributes.hash + post.attributes.thumbnail.data.attributes.ext,
        alt: post.attributes.thumbnail.data.attributes.alternativeText
      }
    }
  })

  return {
    posts: posts,
    limit: data.meta.pagination.limit,
    total: data.meta.pagination.total
  }
}

type Props = {
  featured: Post[],
  posts: Post[],
  pagination: Pagination
}

type Pagination = {
  limit: number,
  total: number
}

const masonryBreakpoints = {
  default: 3,
  1151: 2,
  767: 1
}

const Blog: NextPage<Props> = ({ posts, featured, pagination}) => {
  const [postsState, setPosts] = useState(posts)
  const [isProcessing, setProcessing] = useState(false)

  const loadMorePosts = async () => {
    if (isProcessing) return

    setProcessing(true)

    const { posts } = await getPostsAndFormat(process.env.NEXT_PUBLIC_CMS_API_URL || '', postsState.length, pagination.limit)

    setPosts((currentPostsState) => {
      return [...currentPostsState, ...posts]
    })

    setProcessing(false)
  }

  return (
    <>
      <Head>
        <title>Blogs from Commit Action: Productivity for Entrepreneurs</title>
        <meta name="description" content="Get actionable insights for entrepreneurial success with Commit Action's latest blog posts. Explore practical strategies for personal and professional growth." />
        <link rel="canonical" href={`${process.env.NEXT_PUBLIC_SITE}/post/`} />
        <meta property="og:url" content={`${process.env.NEXT_PUBLIC_SITE}/post/`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Blogs from Commit Action: Productivity for Entrepreneurs" />
        <meta property="og:description" content="Get actionable insights for entrepreneurial success with Commit Action's latest blog posts. Explore practical strategies for personal and professional growth." />
        <meta property="og:image" content={`${process.env.NEXT_PUBLIC_SITE}${blog_page_img.src}`} />
        <meta name="twitter:title" content="Blogs from Commit Action: Productivity for Entrepreneurs" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:description" content="Get actionable insights for entrepreneurial success with Commit Action's latest blog posts. Explore practical strategies for personal and professional growth."/>
        <meta name="twitter:image" content={`${process.env.NEXT_PUBLIC_SITE}${blog_page_img.src}`} />
      </Head>
      <Breadcrumbs values={[{
        name: 'Blog'
      }]}/>
      <Hero screenHeight={false} className="pb-100-to-200">
        <div className="container larger:relative">
          <div className="flex flex-row mt-160-to-340">
            <div className="ml-auto w-full larger:w-auto">
              <FakeNav/>
              <Headline2 tag="h1" className="text-46-to-60 mb-70-to-100">Science Backed <br/> Productivity Training</Headline2>
              <div className="text-18-to-20 flex flex-col sm2:flex-row justify-between w-full larger:w-[700px] xxl:w-[800px]">
                <p className="w-full sm2:w-[45%] mb-[30px] sm2:mb-0">
                  We all know that the personal development space is notorious for pseudoscience-y methods and ‘secrets’ with questionable scientific validity.
                  We took a different approach.
                </p>
                <p className="w-full sm2:w-[45%] xxl:w-[40%] xxl:mr-[40px]">
                Commit Action content was created with an advisory board of Harvard and NYU researchers and academic experts in the fields of neuroscience and empirical psychology.
                </p>
              </div>
              <Button className="mt-14" type="secondary" href={useQuery('/training')}>Get Commit Action’s Core Training</Button>
            </div>
          </div>
          <div className='absolute w-full top-[-30vw] right-[-30vw] larger:right-auto larger:left-0 larger:top-[-130px] larger:w-[400px] sm2:w-[90%] medium:w-[80%] 2xl:w-[33%]'>
            <Image
              loader={loader}
              src={blog_page}
              alt="blog page image"
              priority={true}
            />
          </div>
        </div>
        <PlanetDecor className="h-[900px] larger:h-[1200px] bottom-[-200px] larger:bottom-[-400px] z-[-1]"/>
      </Hero>
      <div className="bg--primary">
        <section className="pt-100-to-200 pb-70-to-130">
          <div className="container container--third">
            <Headline2 tag="h2" className="text-36-to-48 mb-50-to-90">Most read articles</Headline2>
            <Masonry
              breakpointCols={masonryBreakpoints}
              className="masonry-grid"
              columnClassName="masonry-grid_column"
            >
              {featured.map((post: Post) => {
                return <PostTease title={post.title} slug={post.slug} key={post.id} date={post.date} excerpt={post.excerpt} thumbnail={post.thumbnail}/>
              })}
            </Masonry>
          </div>
        </section>
        <section>
          <div className="container container--third">
            <Headline2 tag="h2" className="text-36-to-48 mb-50-to-90">The Latest Articles</Headline2>
            <Masonry
              breakpointCols={masonryBreakpoints}
              className="masonry-grid"
              columnClassName="masonry-grid_column"
            >
              {postsState.map((post: Post) => {
                return <PostTease title={post.title} slug={post.slug} key={post.id} date={post.date} excerpt={post.excerpt} thumbnail={post.thumbnail}/>
              })}
            </Masonry>
            <div className="mt-[20px] sm2:mt-[60px] flex justify-center">
              {postsState.length !== pagination.total && <Button type="secondary" onClick={loadMorePosts}>Load more</Button>}
            </div>
          </div>
        </section>
      </div>
      <CallToAction title="Achieve Your Goals Faster" />
    </>
  )
}

export async function getStaticProps() {
  const queryFeatured = qs.stringify({
    fields: ['title', 'slug', 'excerpt', 'date'],
    populate: {
      thumbnail: {
        fields: ['hash', 'ext', 'alternativeText'],
      },
    },
    filters: {
      featured: {
        $eq: true,
      },
    },
  }, {
    encodeValuesOnly: true
  })

  const resFeatured = await fetch(`${process.env.CMS_API_URL}/posts?${queryFeatured}`)
  const dataFeatured = await resFeatured.json()

  const postsFeatured = dataFeatured.data.map((post: any) => {
    return {
      id: post.id,
      title: post.attributes.title,
      slug: post.attributes.slug,
      date: formatDate(post.attributes.date),
      excerpt: post.attributes.excerpt,
      thumbnail: {
        url: post.attributes.thumbnail.data.attributes.hash + post.attributes.thumbnail.data.attributes.ext,
        alt: post.attributes.thumbnail.data.attributes.alternativeText
      }
    }
  })

  const { posts, limit, total } = await getPostsAndFormat(process.env.CMS_API_URL || '', 0, parseInt(process.env.POSTS_LIMIT || '3'))

  return {
    props: {
      featured: postsFeatured,
      posts: posts,
      pagination: {
        limit: limit,
        total: total
      }
    },
    revalidate: 10
  }
}

export default Blog
